import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Box, Loader2, Shapes, Upload } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { projectsReducer } from '../../store/reducers/projects.reducer';
import { getFolderSelector } from '../../store/selectors/projects.selector';
import { getUser } from '../../store/selectors/user.selector';
import { DrawingSummary } from '../../types/drawing';
import { drawingsApi } from '../drawings/drawingsApi';
import { buildCadProjectForm } from '../new-project-cad/cadProjectRequest';
import { fromDrawingDocument } from '../new-project-cad/drawingDocument';
import { EXAMPLES, ExampleModel, fetchExampleModel } from './exampleModels';
import {
  ModelAnalysis,
  ModelUnit,
  ModelUploadError,
  UNIT_METERS,
  analyzeModels,
  buildModelProjectForm,
  isModelFile,
} from './modelUpload';

type Source = 'model' | 'drawing';

interface NewProjectDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * One step to a project: a name and what it is made from (an uploaded 3D model, an example or a
 * drawing). The project opens in the workspace, where the layers' lighting is set up.
 */
export function NewProjectDialog({ open, onClose }: NewProjectDialogProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const settings = useAppSelector(getUser)?.settings;
  const folder = useAppSelector(getFolderSelector);
  const fileInput = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [source, setSource] = useState<Source>('model');
  const [files, setFiles] = useState<File[]>([]);
  const [analysis, setAnalysis] = useState<ModelAnalysis | null>(null);
  const [unit, setUnit] = useState<ModelUnit>('m');
  const [drawings, setDrawings] = useState<DrawingSummary[] | null>(null);
  const [drawingId, setDrawingId] = useState<number | null>(null);
  const [busy, setBusy] = useState<'reading' | 'creating' | null>(null);
  const [loadingExample, setLoadingExample] = useState<string | null>(null);
  const [selectedExample, setSelectedExample] = useState<string | null>(null);
  // The name this dialog filled in itself: choosing another model replaces it, a typed one stays.
  const autoName = useRef('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || source !== 'drawing' || drawings) return;
    drawingsApi
      .list()
      .then(setDrawings)
      .catch(() => setDrawings([]));
  }, [open, source, drawings]);

  if (!open) return null;

  const chooseFiles = async (chosen: File[], example?: ExampleModel) => {
    const models = chosen.filter(isModelFile);
    setError(null);
    setFiles(models);
    setAnalysis(null);
    setSelectedExample(example?.filename ?? null);
    if (models.length === 0) {
      setError(t('projects.quickCreate.unsupported'));
      return;
    }
    if (!name.trim() || name === autoName.current) {
      autoName.current = example?.name ?? models[0].name.replace(/\.[^.]+$/, '');
      setName(autoName.current);
    }
    setBusy('reading');
    try {
      const result = await analyzeModels(models);
      setAnalysis(result);
      setUnit(result.unit);
    } catch (err) {
      setError(err instanceof ModelUploadError ? t(err.message) : t('projects.quickCreate.readFailed'));
    } finally {
      setBusy(null);
    }
  };

  const createFromModel = async () => {
    if (!analysis) return;
    const form = await buildModelProjectForm({
      name: name.trim() || t('common.untitled'),
      files,
      unit,
      analysis,
      settings,
      folderId: folder?.id,
    });
    dispatch(projectsReducer.actions.createProject(form));
  };

  /** An example is read like an uploaded model: preview, layers and unit, then "Create project". */
  const selectExample = async (example: ExampleModel) => {
    setError(null);
    setLoadingExample(example.filename);
    try {
      await chooseFiles([await fetchExampleModel(example)], example);
    } catch (err) {
      console.error('Loading the example failed:', err);
      setError(t('projects.quickCreate.exampleFailed'));
    } finally {
      setLoadingExample(null);
    }
  };

  const createFromDrawing = async () => {
    if (drawingId === null) return;
    const drawing = await drawingsApi.get(drawingId);
    const plan = fromDrawingDocument(drawing.document);
    if (!plan || plan.polygons.length === 0) {
      setError(t('drawings.emptyDrawing'));
      setBusy(null);
      return;
    }
    const preview = drawing.previewImageUrl ? await toBase64(drawing.previewImageUrl) : '';
    const form = await buildCadProjectForm({
      name: name.trim() || drawing.name,
      plan,
      imageUrl: drawing.referenceImageUrl,
      imageScale: plan.imageScale,
      previewBase64: preview,
      settings,
      folderId: folder?.id,
      sourceDrawingId: drawing.id,
    });
    dispatch(projectsReducer.actions.createProject(form));
  };

  const create = async () => {
    setError(null);
    setBusy('creating');
    try {
      // The project opens in the workspace once it is created.
      if (source === 'model') await createFromModel();
      else await createFromDrawing();
    } catch (err) {
      console.error('Creating the project failed:', err);
      setError(t('drawings.createProjectFailed'));
      setBusy(null);
    }
  };

  // An empty editor, named as typed here; the drawing is stored once something is imported or drawn.
  const newDrawing = () => {
    onClose();
    navigate(name.trim() ? `/drawings/new?name=${encodeURIComponent(name.trim())}` : '/drawings/new');
  };

  const ready = source === 'model' ? !!analysis : drawingId !== null;
  const sourceButton = (value: Source, icon: JSX.Element, label: string) => (
    <button
      onClick={() => {
        setSource(value);
        setError(null);
      }}
      className={`flex-1 flex items-center justify-center gap-2 h-10 rounded-lg text-sm transition-colors ${
        source === value ? 'bg-white text-black' : 'bg-[#2a2a2a] text-gray-300 hover:text-white'
      }`}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={busy ? undefined : onClose} />
      <div className="relative w-full max-w-[520px] mx-4 bg-[#1E1E1E] border border-white/10 rounded-2xl shadow-2xl p-6 text-gray-300">
        <h2 className="text-lg font-semibold text-white mb-4">{t('projects.newProjectModal.title')}</h2>

        <input
          type="text"
          value={name}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          placeholder={t('projects.newProject.projectNamePlaceholder')}
          maxLength={255}
          className="w-full h-10 rounded-lg bg-black border border-black focus:border-lime-400 focus:ring-2 focus:ring-lime-400/30 placeholder-[#747474] text-sm px-4 text-white transition outline-none"
        />

        <div className="flex gap-2 mt-4">
          {sourceButton('model', <Box className="w-4 h-4" />, t('projects.quickCreate.fromModel'))}
          {sourceButton('drawing', <Shapes className="w-4 h-4" />, t('projects.quickCreate.fromDrawing'))}
        </div>

        <div className="mt-4 min-h-[150px]">
          {source === 'model' ? (
            <>
              <input
                ref={fileInput}
                type="file"
                accept=".glb,.obj"
                multiple
                className="hidden"
                onChange={(e) => {
                  void chooseFiles(Array.from(e.target.files ?? []));
                  e.target.value = '';
                }}
              />
              <button
                onClick={() => fileInput.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  void chooseFiles(Array.from(e.dataTransfer.files));
                }}
                className={`relative w-full overflow-hidden border-2 border-dashed border-white/20 hover:border-lime-400/50 rounded-xl flex flex-col items-center justify-center gap-1 text-sm transition-colors ${
                  analysis || busy === 'reading' || loadingExample ? 'h-44 bg-black' : 'h-24'
                }`}
              >
                {analysis ? (
                  <>
                    <img
                      src={`data:image/png;base64,${analysis.thumbnail}`}
                      alt=""
                      className="absolute inset-0 w-full h-full object-contain"
                    />
                    <span className="absolute left-2 bottom-2 max-w-[65%] truncate rounded bg-black/70 px-2 py-0.5 text-[11px] text-white">
                      {files.map((f) => f.name).join(', ')}
                    </span>
                    <span className="absolute right-2 bottom-2 rounded bg-black/70 px-2 py-0.5 text-[11px] text-gray-300">
                      {t('projects.quickCreate.replaceModel')}
                    </span>
                  </>
                ) : busy === 'reading' || loadingExample ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin text-lime-400" />
                    <span className="text-xs text-gray-400">{t('projects.quickCreate.reading')}</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 text-lime-400" />
                    <span className="text-white">{t('projects.quickCreate.chooseModel')}</span>
                    <span className="text-xs text-gray-500">{t('projects.quickCreate.modelFormats')}</span>
                  </>
                )}
              </button>
              {analysis && (
                <div className="mt-3 flex items-center justify-between gap-3 text-xs">
                  <span className="text-gray-400">
                    {t('projects.quickCreate.layersFound', { count: analysis.layerNames.length })}
                  </span>
                  <label className="flex items-center gap-2">
                    <span className="text-gray-400">{t('projects.quickCreate.unit')}</span>
                    <select
                      value={unit}
                      onChange={(e) => setUnit(e.target.value as ModelUnit)}
                      className="h-8 bg-black border border-white/10 rounded px-2 text-white outline-none"
                    >
                      {(Object.keys(UNIT_METERS) as ModelUnit[]).map((u) => (
                        <option key={u} value={u}>
                          {t(`projects.quickCreate.units.${u}`)}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              )}
              <p className="mt-4 mb-2 text-[11px] uppercase tracking-wider text-gray-500">
                {t('projects.quickCreate.examples')}
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {EXAMPLES.map((example) => (
                  <button
                    key={example.filename}
                    onClick={() => selectExample(example)}
                    disabled={!!busy || !!loadingExample}
                    title={example.name}
                    aria-pressed={selectedExample === example.filename}
                    className="group flex flex-col gap-1 text-left disabled:cursor-not-allowed"
                  >
                    <div
                      className={`relative aspect-[8/5] w-full rounded-md overflow-hidden bg-black transition ${
                        selectedExample === example.filename
                          ? 'ring-2 ring-lime-400'
                          : 'ring-1 ring-white/25 group-enabled:group-hover:ring-lime-400/60'
                      }`}
                    >
                      {example.preview ? (
                        <img src={example.preview} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Box className="absolute inset-0 m-auto w-4 h-4 text-gray-600" />
                      )}
                      {loadingExample === example.filename && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <Loader2 className="w-4 h-4 animate-spin text-lime-400" />
                        </div>
                      )}
                    </div>
                    <span className="text-[11px] text-gray-300 truncate">{example.name}</span>
                  </button>
                ))}
              </div>
            </>
          ) : drawings === null ? (
            <p className="text-sm text-gray-500">{t('common.loading')}</p>
          ) : (
            <div className="flex flex-col gap-2 max-h-[220px] overflow-y-auto custom-scrollbar">
              {drawings.map((drawing) => (
                <button
                  key={drawing.id}
                  onClick={() => {
                    setDrawingId(drawing.id);
                    if (!name.trim()) setName(drawing.name);
                  }}
                  className={`flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                    drawingId === drawing.id ? 'bg-lime-400/15 ring-1 ring-lime-400' : 'bg-[#2a2a2a] hover:bg-[#333]'
                  }`}
                >
                  <div className="w-16 h-10 rounded bg-black ring-1 ring-white/25 overflow-hidden flex items-center justify-center shrink-0">
                    {drawing.previewImageUrl ? (
                      <img src={drawing.previewImageUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Shapes className="w-4 h-4 text-gray-600" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">{drawing.name}</p>
                    <p className="text-xs text-gray-500">
                      {t('drawings.polygonCount', { count: drawing.polygonCount })}
                    </p>
                  </div>
                </button>
              ))}
              <button
                onClick={newDrawing}
                disabled={!!busy}
                className="flex items-center gap-3 p-2 rounded-lg text-left bg-transparent border border-dashed border-white/15 hover:border-lime-400/50 text-sm text-gray-300"
              >
                <div className="w-16 h-10 rounded flex items-center justify-center shrink-0">
                  <Shapes className="w-4 h-4 text-lime-400" />
                </div>
                {t('projects.quickCreate.newDrawing')}
              </button>
            </div>
          )}
        </div>

        {error && <p className="mt-3 text-xs text-red-400">{error}</p>}

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={!!busy}
            className="h-9 px-4 rounded-full text-xs font-medium border border-white/20 text-gray-200 hover:border-white/40 disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={create}
            disabled={!ready || !!busy}
            className="h-9 px-5 rounded-full text-xs font-medium bg-lime-400 text-black hover:bg-lime-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy === 'creating' ? t('projects.cadProject.creating') : t('projects.quickCreate.create')}
          </button>
        </div>
      </div>
    </div>
  );
}

async function toBase64(url: string): Promise<string> {
  try {
    const blob = await (await fetch(url)).blob();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    return dataUrl.slice(dataUrl.indexOf(',') + 1);
  } catch {
    return '';
  }
}
